import { useEffect, useMemo, useRef, useState } from 'react';
import { LiksConfirmDialog } from '../LiksConfirmDialog/LiksConfirmDialog';
import { LiksSearch } from '../LiksSearch/LiksSearch';
import { formatDirectoryUserDisplayName } from '../../../features/user/userDisplay';
import type { DirectoryUser } from '../../../types/user';
import { LiksMembersInviteRow } from './LiksMembersInviteRow';
import { LiksMembersMemberRow } from './LiksMembersMemberRow';
import { withName } from './membersPanelUtils';
import type {
  LiksMembersPanelMember,
  LiksMembersPanelProps,
  MemberCardModel,
  PendingRemove,
} from './LiksMembersPanel.types';
import './LiksMembersPanel.css';

export type {
  LiksMembersPanelLabels,
  LiksMembersPanelMember,
  LiksMembersPanelProps,
  LiksMembersPanelRoleOption,
} from './LiksMembersPanel.types';

export function LiksMembersPanel<TRole extends string>({
  resourceName,
  members,
  directoryUsers,
  isLoadingDirectoryUsers,
  directoryUsersError,
  roleOptions,
  defaultInviteRole,
  currentUserId,
  canManageMembers,
  canLeave,
  canManageMember,
  adminRole,
  labels,
  pendingUserId,
  isLeaving,
  onInvite,
  onRoleChange,
  onRemove,
  onLeave,
}: LiksMembersPanelProps<TRole>) {
  const [searchQuery, setSearchQuery] = useState('');
  const [inviteRoles, setInviteRoles] = useState<
    Partial<Record<string, TRole>>
  >({});
  const [pendingRemove, setPendingRemove] = useState<PendingRemove | null>(
    null,
  );
  const [isLeaveDialogOpen, setIsLeaveDialogOpen] = useState(false);
  const previousPendingUserIdRef = useRef(pendingUserId);

  const isRemoveBusy =
    pendingRemove !== null && pendingUserId === pendingRemove.member.userId;

  useEffect(() => {
    const previousPendingUserId = previousPendingUserIdRef.current;
    previousPendingUserIdRef.current = pendingUserId;

    if (
      pendingRemove != null &&
      previousPendingUserId === pendingRemove.member.userId &&
      pendingUserId === null
    ) {
      setPendingRemove(null);
    }
  }, [pendingRemove, pendingUserId]);

  const usersById = useMemo(() => {
    const map = new Map<string, DirectoryUser>();
    for (const directoryUser of directoryUsers) {
      if (directoryUser.id) {
        map.set(directoryUser.id, directoryUser);
      }
    }
    return map;
  }, [directoryUsers]);

  const membersByUserId = useMemo(() => {
    const map = new Map<string, LiksMembersPanelMember<TRole>>();
    for (const member of members) {
      map.set(member.userId, member);
    }
    return map;
  }, [members]);

  const memberCards = useMemo((): MemberCardModel<TRole>[] => {
    return members
      .map((member) => {
        const directoryUser = usersById.get(member.userId);
        return {
          member,
          displayName: formatDirectoryUserDisplayName(
            directoryUser,
            labels.unknownUser,
          ),
          email: directoryUser?.email ?? null,
          thumbnail: directoryUser?.thumbnail ?? null,
        };
      })
      .sort((a, b) => a.displayName.localeCompare(b.displayName));
  }, [labels.unknownUser, members, usersById]);

  const adminCount = useMemo(
    () => members.filter((m) => m.role === adminRole).length,
    [members, adminRole],
  );

  const isLastAdmin = (member: LiksMembersPanelMember<TRole>): boolean =>
    member.role === adminRole && adminCount <= 1;

  const otherUsers = useMemo(() => {
    if (!canManageMembers) {
      return [];
    }

    return directoryUsers
      .filter(
        (directoryUser) =>
          typeof directoryUser.id === 'string' &&
          directoryUser.id.length > 0 &&
          !membersByUserId.has(directoryUser.id),
      )
      .map((directoryUser) => ({
        user: directoryUser,
        displayName: formatDirectoryUserDisplayName(
          directoryUser,
          labels.unknownUser,
        ),
        email: directoryUser.email ?? null,
      }))
      .sort((a, b) => a.displayName.localeCompare(b.displayName));
  }, [canManageMembers, directoryUsers, labels.unknownUser, membersByUserId]);

  const filteredOtherUsers = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (query === '') {
      return otherUsers;
    }

    return otherUsers.filter(
      (entry) =>
        entry.displayName.toLowerCase().includes(query) ||
        (entry.email?.toLowerCase().includes(query) ?? false),
    );
  }, [otherUsers, searchQuery]);

  const getInviteRole = (userId: string): TRole =>
    inviteRoles[userId] ?? defaultInviteRole;

  if (isLoadingDirectoryUsers) {
    return (
      <section className="liks-members">
        <p className="liks-members__status">{labels.loading}</p>
      </section>
    );
  }

  if (directoryUsersError) {
    return (
      <section className="liks-members">
        <p className="liks-members__status">{labels.loadError}</p>
      </section>
    );
  }

  return (
    <>
      <section className="liks-members">
        <div className="liks-members__section">
          <h3 className="liks-members__section-title">
            {labels.membersHeading}
          </h3>
          {memberCards.length === 0 ? (
            <p className="liks-members__empty">{labels.membersEmpty}</p>
          ) : (
            <ul className="liks-members__list">
              {memberCards.map(({ member, displayName, email, thumbnail }) => (
                <LiksMembersMemberRow
                  key={member.id}
                  member={member}
                  displayName={displayName}
                  email={email}
                  thumbnail={thumbnail}
                  isCurrentUser={member.userId === currentUserId}
                  manageable={canManageMember(member)}
                  lastAdmin={isLastAdmin(member)}
                  canLeave={canLeave}
                  resourceName={resourceName}
                  roleOptions={roleOptions}
                  labels={labels}
                  pendingUserId={pendingUserId}
                  isLeaving={isLeaving}
                  onRoleChange={onRoleChange}
                  onRequestRemove={(targetMember, label) => {
                    setPendingRemove({
                      member: {
                        id: targetMember.id,
                        userId: targetMember.userId,
                      },
                      label,
                    });
                  }}
                  onRequestLeave={() => setIsLeaveDialogOpen(true)}
                />
              ))}
            </ul>
          )}
        </div>

        {canManageMembers && (
          <div className="liks-members__section">
            <h3 className="liks-members__section-title">
              {labels.othersHeading}
            </h3>
            <LiksSearch
              value={searchQuery}
              onChange={setSearchQuery}
              placeholder={labels.searchPlaceholder}
              aria-label={labels.searchLabel}
              clearLabel={labels.searchClear}
              variant="on-light"
            />
            {filteredOtherUsers.length === 0 ? (
              <p className="liks-members__empty">
                {searchQuery.trim() === ''
                  ? labels.othersEmpty
                  : labels.inviteNoResults}
              </p>
            ) : (
              <ul className="liks-members__list">
                {filteredOtherUsers.map(
                  ({ user: directoryUser, displayName, email }) => {
                    const userId = directoryUser.id!;
                    return (
                      <LiksMembersInviteRow
                        key={userId}
                        directoryUser={directoryUser}
                        displayName={displayName}
                        email={email}
                        inviteRole={getInviteRole(userId)}
                        roleOptions={roleOptions}
                        labels={labels}
                        pendingUserId={pendingUserId}
                        onInviteRoleChange={(id, role) =>
                          setInviteRoles((current) => ({
                            ...current,
                            [id]: role,
                          }))
                        }
                        onInvite={onInvite}
                      />
                    );
                  },
                )}
              </ul>
            )}
          </div>
        )}
      </section>

      <LiksConfirmDialog
        isOpen={isLeaveDialogOpen}
        title={labels.leaveTitle}
        message={withName(labels.leaveMessage, resourceName)}
        confirmLabel={labels.leaveConfirm}
        cancelLabel={labels.leaveCancel}
        isBusy={isLeaving}
        onConfirm={() => {
          if (isLeaving) {
            return;
          }
          onLeave();
        }}
        onCancel={() => {
          if (!isLeaving) {
            setIsLeaveDialogOpen(false);
          }
        }}
      />

      <LiksConfirmDialog
        isOpen={pendingRemove !== null}
        title={labels.removeMemberTitle}
        message={withName(
          labels.removeMemberMessage,
          pendingRemove?.label ?? labels.unknownUser,
        )}
        confirmLabel={labels.removeConfirm}
        cancelLabel={labels.removeCancel}
        isBusy={isRemoveBusy}
        onConfirm={() => {
          if (!pendingRemove || isRemoveBusy) {
            return;
          }
          const member = members.find(
            (entry) => entry.id === pendingRemove.member.id,
          );
          if (member) {
            onRemove(member);
          }
        }}
        onCancel={() => {
          if (!isRemoveBusy) {
            setPendingRemove(null);
          }
        }}
      />
    </>
  );
}
